import { useCallback, useEffect, useState } from 'react'
import { KeyRound, Plus, ShieldCheck } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { PERMISSIONS } from '@/lib/permissions'
import { PermissionDenied, ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import { fetchRoles, createRole, ORG_STR as S, type RoleRow } from './staffOrgApi'

/**
 * A23.05 — Rol yaratish/tahrirlash. Rol katalogi JONLI (GET /admin/roles); yangi
 * rol yaratish greenfield POST /admin/roles — endpoint chiqqanda ishga tushadi
 * (hozir 404 → toast, katalog buzilmaydi). RBAC majburlash serverda.
 */
export function RoleEditorScreen() {
  const { can } = useAuth()
  const { toast } = useToast()
  const [roles, setRoles] = useState<RoleRow[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  const [name, setName] = useState('')
  const [perms, setPerms] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchRoles(ac.signal)
      .then(setRoles)
      .catch((e) => {
        if (!ac.signal.aborted)
          setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick])

  const submit = async () => {
    const role = name.trim()
    if (!role) return
    const list = perms
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean)
    setBusy(true)
    try {
      await createRole(role, list)
      setName('')
      setPerms('')
      toast(S.created, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  if (!can(PERMISSIONS.ROLE_VIEW)) return <PermissionDenied permission={PERMISSIONS.ROLE_VIEW} />

  return (
    <>
      <PageHeader title={S.roleEditor} screenId="A23.05" description={S.roleEditorDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {/* Yangi rol qoralamasi */}
          {can(PERMISSIONS.ROLE_EDIT) && (
            <div className="rounded-lg border border-border-default bg-surface-2 p-3">
              <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-secondary">
                <Plus size={14} />
                {S.newRole}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={S.roleName}
                  className="min-w-[160px] rounded-md border border-border-default bg-surface-1 px-3 py-2 font-mono text-sm text-primary outline-none focus:border-accent"
                />
                <input
                  value={perms}
                  onChange={(e) => setPerms(e.target.value)}
                  placeholder={S.permissions}
                  className="min-w-[220px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
                />
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy || !name.trim()}
                  className="flex items-center gap-1 rounded-md bg-accent px-3 py-2 text-sm text-white disabled:opacity-50"
                >
                  <Plus size={14} />
                  {S.create}
                </button>
              </div>
              <p className="mt-1.5 text-xs text-tertiary">{S.createHint}</p>
            </div>
          )}

          {/* Rol katalogi (jonli) */}
          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !roles ? (
            <div className="space-y-1.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : roles.length === 0 ? (
            <EmptyState icon={<ShieldCheck size={28} strokeWidth={1.5} />} />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border-default">
              {roles.map((r, i) => (
                <div
                  key={r.role}
                  className={`flex items-center gap-3 px-4 py-2.5 ${i > 0 ? 'border-t border-border-subtle' : ''}`}
                >
                  <KeyRound size={14} className="shrink-0 text-tertiary" />
                  <span className="w-56 shrink-0 font-mono text-sm text-primary">{r.role}</span>
                  <span className="min-w-0 flex-1 font-mono text-sm text-secondary tabular">
                    {r.all ? S.allPerms : `${r.permission_count} ${S.permissions.toLowerCase()}`}
                  </span>
                  {r.assignable && <span className="text-xs text-success">● {S.assignable}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
